import { Injectable, computed, inject, signal } from '@angular/core';
import { LoggingService } from './logging.service';
import { TrilingualInput } from '../models/trilingual-input.model';
import { I18n } from 'aws-amplify/utils';
import { translations } from '@aws-amplify/ui';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  public emailIsValid(email: string): boolean {
    if (email.length === 0) {
      return true;
    }

    const valid = email.match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

    if (!valid) {
      return false;
    }

    return true;
  }

  public phoneIsValid(phone: string): boolean {
    const valid = phone.match(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{1,5}$/im);

    if (!valid) {
      return false;
    }

    return true;
  }

  public getLocalizedLabel(
    obj: { [x: string]: string | number | boolean | null | undefined | TrilingualInput } | undefined,
    propertyName: string,
    lang = this.locale(),
  ): string {
    if (!obj || !propertyName) {
      this.logger.warn(`Root object and property are mandatory to get localized label`, obj, propertyName);
      return '';
    }

    if (lang === 'en') {
      if (!Object.prototype.hasOwnProperty.call(obj, propertyName)) {
        return obj[propertyName] as string;
      }

      return obj[propertyName] as string;
    } else if (lang === 'es' || lang === 'fr') {
      if (!Object.prototype.hasOwnProperty.call(obj, `${propertyName}_${lang}`)) {
        return obj[propertyName] as string;
      }

      return obj[`${propertyName}_${lang}`] as string;
    }

    throw new Error('Unsupported language ' + lang);
  }

  public setLocale(value: string): void {
    if (value !== 'en' && value !== 'fr' && value !== 'es') {
      this.localeSignal.update(() => 'en');
      this.setAmplifyLanguage('en');
    } else {
      this.localeSignal.update(() => value);
      this.setAmplifyLanguage(value);
    }
  }

  private setAmplifyLanguage(language: string): void {
    I18n.setLanguage(language);
    I18n.putVocabularies(translations);
  }

  public setCurrentRoute(route: string): void {
    this.currentRouteSignal.update(() => route);
  }

  private readonly logger = inject(LoggingService);
  private readonly localeSignal = signal<string>('en');
  private readonly currentRouteSignal = signal<string>('summary_ui');

  public locale = computed(() => this.localeSignal());
  public currentRoute = this.currentRouteSignal.asReadonly();
  public carbonTitle = signal<string>($localize`Carbon sequestration`);
  public carbonDescription = signal<string>(
    $localize`This is the number of tonnes of CO2 equivalent that you have helped to sequester, and which also corresponds to your quantity of certified carbon offset credits. The sequestered carbon comes from new forests and protected forests, thanks to your investment.`,
  );
  public capitalGainsTitle = signal<string>($localize`Natural capital gains`);
  public capitalGainsDescription = signal<string>(
    $localize`Natural capital represents the economic value of goods and services derived from nature. Wood, food, biodiversity, water and air quality are examples. The gain in natural capital is negative during the first years since the preliminary land preparation work requires removing some existing vegetation. As new forests grow and the ecosystem regenerates, natural capital gains increase.`,
  );
  public damageAvoidedTitle = signal<string>($localize`Economic damage avoided`);
  public damageAvoidedDescription = signal<string>(
    $localize`This graph shows the amount corresponding to the environmental damages avoided, thanks to your investment. The damage caused by climate change translates into negative economic value. These can include effects on human health, property damage from floods and natural disasters, or lower agricultural productivity.`,
  );
  public landownersIncomeTitle = signal<string>($localize`Landowners' income`);
  public landownersIncomeDescription = signal<string>(
    $localize`This amount corresponds to the income that the landowners involved in our projects receive, depending on your investment. By comparison, the average annual salary in Peru was US $4,693.32 in 2021. Source: <a href="https://preciosmundi.com/peru/precio-vivienda-salarios" class="text-vti-500 hover:text-vti-700" target="_blank">https://preciosmundi.com/peru/precio-vivienda-salarios</a>`,
  );
  public familiesInvolvedTitle = signal<string>($localize`Families involved`);
  public familiesInvolvedDescription = signal<string>(
    $localize`Every time we restore land, a farmer land-owner and his family benefit. Whether it is through higher income or of children pursuing studies, entire families benefit from the impact of the projects.`,
  );
  public moneyDistributedTitle = signal<string>($localize`Money distributed`);
  public moneyDistributedDescription = signal<string>(
    $localize`Each year, you will receive cash distributions (also known as dividends) corresponding to your return on investment from project profits. The discounted return on investment (ROI) and internal rate of return (IRR) are calculated based on this distributed sum.`,
  );
  public biologicalAssetsTitle = signal<string>($localize`Value of biological assets`);
  public biologicalAssetsDescription = signal<string>(
    $localize`By investing in TreesOfLives, you get units (shares) of the limited partnership that owns a co-ownership with the landowners on the tree-related biological assets established on the restored land. These trees produce goods and services of commercial value over time, thus creating monetary value in the future. We considered the discounted cash flow formula with a conservative 20% rate to calculate the value of the assets. When you sell your units, the value of the biological assets could be a starting point for negotiations with other investors eventually.`,
  );
  public qualityHectareTitle = signal<string>($localize`Quality-Hectare`);
  public qualityHectareDescription = signal<string>(
    $localize`The Quality-hectare (QH) index is used to quantify the biodiversity gained or lost in a standardized and transparent manner. This is the product of the area of the ecosystem studied in hectares multiplied by a Quality index varying from 0 to 1. Based on a series of indicators measured in the field, this index makes it possible to measure the difference in naturalness between the ecosystem studied and an intact reference ecosystem.`,
  );
  public startDateTitle = signal<string>($localize`Start date`);
  public startDateDescription = signal<string>(
    $localize`Corresponds to the start date for restoration work on all the properties included in this investment project(s).`,
  );
  public areaRestoredTitle = signal<string>($localize`Hectares restored`);
  public areaRestoredDescription = signal<string>(
    $localize`Corresponds to the total surface area of the plots on which one of our restoration systems has been implemented.`,
  );
  public areaProtectedTitle = signal<string>($localize`Hectares protected`);
  public areaProtectedDescription = signal<string>(
    $localize`Corresponds to the total surface area of the plots for which we have signed a conservation agreement with the owners.`,
  );
  public investmentTitle = signal<string>($localize`Value of your investment`);
  public investmentDescription = signal<string>($localize`Total amount invested in these investment project(s).`);
}
